import { Component, Input, TemplateRef, ViewChild } from '@angular/core';
import { DocumentsCategoriesBySection, SnapshotDisplayMode } from '@common/constants';
import { DvSnapshot, DvSnapshotField } from '@common/entities';
import { Utilities } from '@common/utils';
import { IdpDataAlbertaCa } from '@govalta-emu/keycloak-auth-service';

@Component({
  standalone: false,
  selector: 'common-application-snapshot-display',
  templateUrl: './application-snapshot-display.component.html',
  styleUrls: ['./application-snapshot-display.component.scss'],
})
export class ApplicationSnapshotDisplayComponent {
  loading = true;
  utils = Utilities;

  displayMode = SnapshotDisplayMode;
  _applicationData: DvSnapshot;
  accordionsOpen: string[] = [];

  @ViewChild('displayModeNormal', { static: true }) displayModeNormal: TemplateRef<unknown>;
  @ViewChild('displayModeGrid', { static: true }) displayModeGrid: TemplateRef<unknown>;
  @ViewChild('displayModeGridTwoColumn', { static: true }) displayModeGridTwoColumn: TemplateRef<unknown>;
  @ViewChild('displayModeMultiInput', { static: true }) displayModeMultiInput: TemplateRef<unknown>;

  @Input() signerProfile: IdpDataAlbertaCa;
  @Input() signerProfileStep = '';
  @Input() applicantProfile: IdpDataAlbertaCa;
  @Input() applicantProfileStep = '';

  //document category for files passed in must be grouped by corresponding section name (ex. personalInformation.identification).
  @Input() documentCategoryLookup: DocumentsCategoriesBySection;

  @Input() set applicationData(value: DvSnapshot) {
    if (Utilities.hasValue(value)) {
      this._applicationData = value;
    }
  }
  get applicationData() {
    return this._applicationData;
  }

  fieldsToDisplay(dataFields: DvSnapshotField[]) {
    return dataFields.filter((d) => d.snapshotValue?.displayMode !== SnapshotDisplayMode.Documents);
  }

  //returns a list of documentList
  //if documentList is inside of a collection (needs to be rendered inline with data instead of at the end) then it should be flagged as DisplayMode.NestedDocuments
  //default is there is only 1 document list at the end of the page and is redered as DisplayMode.Documents
  documentListCollection(
    dataFields: DvSnapshotField[],
    documentDisplayMode: number = SnapshotDisplayMode.Documents,
    index = -1
  ) {
    const docList = dataFields
      .filter((d) => d.snapshotValue?.displayMode === documentDisplayMode)
      .map((i) => i.snapshotValue); //only take 1 (should only ever have 1 documentList object)

    let documentListCollection = docList?.length > 0 ? docList : [];
    //if a specific documentList index is requested, only return that document as a collection of 1
    if (index >= 0 && documentListCollection?.length > 0) {
      documentListCollection = [documentListCollection[index]];
    }
    return documentListCollection;
  }

  getTemplateForDisplayMode(displayMode: SnapshotDisplayMode): TemplateRef<unknown> | null {
    switch (displayMode) {
      case SnapshotDisplayMode.Normal:
        return this.displayModeNormal;
      case SnapshotDisplayMode.Grid:
        return this.displayModeGrid;
      case SnapshotDisplayMode.GridTwoColumn:
        return this.displayModeGridTwoColumn;
      case SnapshotDisplayMode.MultiInput:
        return this.displayModeMultiInput;
      default:
        return null;
    }
  }
  /**
   * Groups fields by subHeader, including null (no subHeader).
   */
  groupFieldsBySubHeader(fields: DvSnapshotField[]): { subHeader?: string; fields: DvSnapshotField[] }[] {
    const groupMap = new Map<string, DvSnapshotField[]>();
    const NO_SUBHEADER_KEY = '__no_subheader__';

    for (const field of fields) {
      const key = field.snapshotValue?.subHeader ?? NO_SUBHEADER_KEY;
      if (!groupMap.has(key)) {
        groupMap.set(key, []);
      }
      groupMap.get(key).push(field);
    }

    const grouped: { subHeader?: string; fields: DvSnapshotField[] }[] = [];

    for (const [subHeader, fields] of groupMap.entries()) {
      grouped.push({
        subHeader: subHeader === NO_SUBHEADER_KEY ? undefined : subHeader,
        fields,
      });
    }
    return grouped;
  }

  getCategoryLookup(section) {
    const categoryLookup = this.documentCategoryLookup[section];
    if (Object.keys(categoryLookup)?.length > 0) {
      return categoryLookup;
    }
    return null;
  }

  toggleAccordion(code: string, event: Event) {
    const isOpen = (event as CustomEvent)?.detail?.open ?? false;
    if (isOpen) {
      // Add code if it's not already in the array
      if (!this.accordionsOpen.includes(code)) {
        this.accordionsOpen.push(code);
      }
    } else {
      // Remove code from the array
      this.accordionsOpen = this.accordionsOpen.filter((c) => c !== code);
    }
  }

  isExpanded(code: string): boolean {
    return this.accordionsOpen.includes(code);
  }

  trackByGroup(index: number, group: { groupCode?: string }): string | number {
    return group?.groupCode ?? index;
  }

  trackByField(index: number, field: DvSnapshotField): string | number {
    return field?.fieldReference ?? index;
  }

  trackByValueItem(index: number, itemValue: { fieldName?: string; label?: string }): string | number {
    return itemValue?.fieldName ?? itemValue?.label ?? index;
  }

  trackByDocumentList(index: number, docList: { fieldName?: string; label?: string }): string | number {
    return docList?.fieldName ?? docList?.label ?? index;
  }

  trackByIndex(index: number): number {
    return index;
  }
}
